import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faUsers,
  faFileLines,
  faBrain,
  faExclamationTriangle,
  faCheckCircle,
  faShieldAlt,
  faCommentDots,
  faHome,
} from "@fortawesome/free-solid-svg-icons";
import ClientActionCard from "./ClientActionCard"; // Import the new action card component
import ClientDetailScoreCard from "./ClientDetailScoreCard"; // Import the new detailed scorecard
import "./ClientView.css";
import { fetchClient, fetchLatestVisitNote } from "../utils/api";
import { formatYyyyDdMm } from "../utils/date";

const ClientView = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();

  const [clientData, setClientData] = useState({
    id: clientId,
    firstName: "",
    lastName: "",
    folderId: "",
    folderName: "Folder",
    locationId: "",
    locationName: "",
    wellBeingScore: 0,
    careTeamScore: 0,
    environment: 0,
    aipScore: 0,
    latestAssessmentStatus: "",
    isAipProvisional: false,
  });
  const [latestNote, setLatestNote] = useState(null);

  useEffect(() => {
    const loadClient = async () => {
      try {
        const client = await fetchClient(clientId);
        // Normalize folder info in case API returns a populated folder object
        const folderObj = client.folderId;
        const locationObj = client.locationId;
        const folderId =
          typeof folderObj === "object"
            ? folderObj.id || folderObj._id || ""
            : folderObj || "";
        const folderName =
          typeof folderObj === "object"
            ? folderObj.name || folderObj.year || "Folder"
            : folderId || "Folder";
        const locationId =
          typeof locationObj === "object"
            ? locationObj.id || locationObj._id || ""
            : locationObj || "";
        const locationName =
          typeof locationObj === "object"
            ? locationObj.name || locationObj.label || ""
            : "";

        setClientData({
          id: client.id || client._id || clientId,
          firstName: client.firstName || "",
          lastName: client.lastName || "",
          folderId,
          folderName,
          locationId,
          locationName,
          wellBeingScore: client.wellBeingScore || 0,
          careTeamScore: client.careTeamScore || 0,
          environment: client.environment || 0,
          aipScore: client.aipScore || 0,
          latestAssessmentStatus: client.latestAssessmentStatus || "",
          isAipProvisional: client.isAipProvisional === true,
        });
      } catch (err) {
        console.error("Failed to load client", err);
      }
    };
    if (clientId) loadClient();
  }, [clientId]);

  useEffect(() => {
    const loadLatestNote = async () => {
      try {
        const note = await fetchLatestVisitNote(clientId);
        setLatestNote(note);
      } catch (err) {
        console.error("Failed to load latest visit note", err);
      }
    };
    if (clientId) loadLatestNote();
  }, [clientId]);

  const fullName =
    `${clientData.firstName} ${clientData.lastName}`.trim() || "Client";

  const scoreDisplay = (score) => {
    const val = Number(score) || 0;
    const status =
      val >= 75
        ? { text: "Good", cls: "green", iconCls: "green-text" }
        : val >= 40
          ? { text: "Moderate", cls: "orange", iconCls: "orange-text" }
          : { text: "Needs Data", cls: "red", iconCls: "red-text" };
    return { val, status };
  };

  const wb = scoreDisplay(clientData.wellBeingScore);
  const ct = scoreDisplay(clientData.careTeamScore);
  const es = scoreDisplay(clientData.environment);
  const aip = scoreDisplay(clientData.aipScore);
  const isAipProvisional =
    clientData.isAipProvisional === true ||
    String(clientData.latestAssessmentStatus || "").toLowerCase() ===
      "auto-provisional";

  const handleAipAnalysisClick = () => {
    navigate(`/client-management/client/${clientId}/aip-analysis`);
  };

  const folderBreadcrumbHref = clientData.folderId
    ? `/client-management/${clientData.folderId}`
    : clientData.locationId
      ? `/client-management/location/${clientData.locationId}`
      : "/client-management";
  const folderBreadcrumbLabel =
    clientData.folderName || clientData.locationName || "Location";

  return (
    <div className="client-view-container">
      <div className="client-view-header">
        <div className="breadcrumb">
          <FontAwesomeIcon icon={faArrowLeft} />{" "}
          <Link to="/client-management">Home</Link> &gt;
          <Link to={folderBreadcrumbHref}>{folderBreadcrumbLabel}</Link> &gt;
          <span>
            <FontAwesomeIcon icon={faUsers} /> {fullName}
          </span>
        </div>

        <button
          className="ask-albert-btn-luxury header-btn"
          onClick={() =>
            navigate("/ask-albert", {
              state: {
                locationId: clientData.locationId || null,
                contextLabel: fullName,
                starterQuestion: `What is the most recent shift report note for ${fullName}?`,
                autoSend: true,
              },
            })
          }
        >
          <FontAwesomeIcon icon={faCommentDots} /> Ask Albert about client{" "}
          {clientData.clientNumber ?? clientData.id}
        </button>
      </div>

      <div className="client-view-content-wrapper">
        <div className="client-view-main-content">
          {isAipProvisional ? (
            <div className="client-provisional-banner">
              This AIP score is provisional and based on WellSky care plan data
              only. Care Team and Environmental Safety sections require a full
              assessment.
            </div>
          ) : null}
          {/* Luxury Scores Bar */}
          <div className="luxury-scores-bar">
            <div className="scores-group">
              {/* Well-Being Score */}
              <div className="score-item">
                <div className="score-icon-wrapper gray-circle">
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    className="score-icon-alert red-text"
                  />
                </div>
                <div className="score-details">
                  <span className="score-title">Well-Being Score</span>
                  <div className="score-value-row">
                    <span className={`score-val ${wb.status.iconCls}`}>
                      {wb.val > 0 ? `${wb.val} / 100` : "-- / 100"}
                    </span>
                    <span className={`status-badge ${wb.status.cls}`}>
                      {wb.status.text}
                    </span>
                  </div>
                </div>
              </div>

              <div className="score-divider"></div>

              {/* Care Team Score */}
              <div className="score-item">
                <div className="score-icon-wrapper green-ring">
                  <span className="score-circle-text green-text">83</span>
                </div>
                <div className="score-details">
                  <span className="score-title">Care Team Score</span>
                  <div className="score-value-row">
                    <span className={`score-val ${ct.status.iconCls}`}>
                      {ct.val > 0 ? `${ct.val} / 100` : "-- / 100"}
                    </span>
                    <span className={`status-badge ${ct.status.cls}`}>
                      {ct.status.text}
                    </span>
                  </div>
                </div>
              </div>

              <div className="score-divider"></div>

              {/* Environment Score - NEW in v2.0 */}
              <div className="score-item">
                <div className="score-icon-wrapper gray-circle">
                  <FontAwesomeIcon
                    icon={faHome}
                    className="score-icon-alert"
                    style={{
                      color:
                        es.val >= 75
                          ? "#52c41a"
                          : es.val >= 40
                            ? "#faad14"
                            : "#ff4d4f",
                    }}
                  />
                </div>
                <div className="score-details">
                  <span className="score-title">Environment Score</span>
                  <div className="score-value-row">
                    <span className={`score-val ${es.status.iconCls}`}>
                      {es.val > 0 ? `${es.val} / 100` : "-- / 100"}
                    </span>
                    <span className={`status-badge ${es.status.cls}`}>
                      {es.status.text}
                    </span>
                  </div>
                </div>
              </div>

              <div className="score-divider"></div>

              {/* AIP Score */}
              <div className="score-item">
                <div className="score-icon-wrapper gray-circle">
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    className="score-icon-alert red-text"
                  />
                </div>
                <div className="score-details">
                  <span className="score-title">
                    AIP Score{" "}
                    {isAipProvisional ? (
                      <span className="score-provisional-tag">Provisional</span>
                    ) : null}
                  </span>
                  <div className="score-value-row">
                    <span className={`score-val ${aip.status.iconCls}`}>
                      {aip.val > 0 ? `${aip.val} / 100` : "-- / 100"}
                    </span>
                    <span className={`status-badge ${aip.status.cls}`}>
                      {aip.status.text}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="middle-action-cards-grid">
            <ClientActionCard
              icon={faFileLines}
              iconBgColor="#faad14"
              title="Shift Report Transcribed Note"
              description="View conversation that the caregiver had with the shift reporting agent regarding their shift."
              hoverDetails={[
                `Last Visit: ${latestNote?.date ? formatYyyyDdMm(latestNote.date) : "--"}`,
                `Caregiver: ${latestNote?.caregiverName || "--"}`,
                `Status: ${latestNote ? "Completed" : "--"}`,
              ]}
              onClick={() =>
                navigate(
                  `/client-management/client/${clientId}/aip-analysis?tab=visitNote`,
                )
              }
            />
            <ClientActionCard
              icon={faBrain}
              iconBgColor="#8c8c8c"
              title="AIP Smart Care Analysis"
              description="Calculate aging in place scores"
              hoverDetails={[
                "Next Analysis: Nov 01, 2025",
                "Risk Level: Medium",
                "Trend: Stable",
              ]}
              onClick={handleAipAnalysisClick}
            />
          </div>
        </div>

        <div className="client-view-sidebar">
          <ClientDetailScoreCard
            icon={faShieldAlt}
            iconBgColor="#1890ff"
            title="Well-Being Score"
            description="Health, function, and lifestyle assessment"
            currentScore={wb.val}
            maxScore={100}
            statusBadges={[{ text: wb.status.text, type: wb.status.cls }]}
            alertIcon={faExclamationTriangle}
            progressColor="#faad14"
          />
          <ClientDetailScoreCard
            icon={faUsers}
            iconBgColor="#52c41a"
            title="Care Team Score"
            description="Service delivery and coordination metrics"
            currentScore={ct.val}
            maxScore={100}
            statusBadges={[{ text: ct.status.text, type: ct.status.cls }]}
            alertIcon={faCheckCircle}
            progressColor="#52c41a"
          />
          <ClientDetailScoreCard
            icon={faBrain}
            iconBgColor="#f5222d"
            title={isAipProvisional ? "AIP Score (Provisional)" : "AIP Score"}
            description="Calculate aging in place (AIP) score"
            currentScore={aip.val}
            maxScore={100} // Assuming 100 is max, adjust as needed
            statusBadges={[{ text: aip.status.text, type: aip.status.cls }]}
            alertIcon={faExclamationTriangle}
            progressColor="#f5222d"
            showAskAlbert={false} // Hidden in detail view as it's now in the main bar
          />
        </div>
      </div>
    </div>
  );
};

export default ClientView;
